<template>
  <form @submit.prevent="submitForm" class="homeQuickSearchForm ts-form ts-border-radius__md ts-borderless-inputs">
    <div class="flex flex-col items-center px-5 md:px-0">
      <div class="inline-flex items-center justify-center mb-6">
        <!-- Radio Group for Listing Type (Sale or Rent) -->
        <RadioGroupComponent
            v-model="form.listingType"
            :sale-label="props.listingTypeSale"
            :rent-label="props.listingTypeRental"
        />
      </div>
      <div class="select-menu w-full lg:flex lg:flex-wrap items-center p-2 md:px-2 lg:p-4 bg-surface-color-100 rounded-l-[20px] rounded-r-[20px] box-shadow">
        <!-- Dropdown for Category Selection -->
        <Listbox
            v-model="selectedCategory"
            @change="updateFormCategory"
            :class="{ 'class-when-open': open }"
            v-slot="{ open }"
        >
          <div class="lg:flex w-auto relative gap-[1px] pb-1 lg:pb-0">
            <div>
              <ListboxButton
                  :class="{ '!ring-[2px] !ring-border-color-200': open }"
                  class="p-5 relative w-[320px] md:w-[512px] lg:w-[192px] flex justify-between items-center rounded-lg text-left gap-3 ring-0 ring-border-color-dark-200 hover:bg-surface-color-300 focus:ring-[2px] focus:ring-border-color-200 bg-surface-color-100"
              >
                <span class="block truncate text-text-color-light-100 text-base font-medium">{{ selectedCategory.n }}</span>
                <svg :class="{ 'rotate-180 relative': open }" class="w-4 h-4 fill-zinc-600">
                  <use xlink:href="#search-arrow" href="#search-arrow"></use>
                </svg>
              </ListboxButton>
              <transition
                  leave-active-class="transition duration-100 ease-in"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
              >
                <div v-if="open" class="z-50 absolute mt-2 w-full min-w-[124px] rounded-lg bg-surface-color-100 box-shadow border border-border-color-light-300 focus:outline-none sm:text-sm">
                  <div class="p-2">
                    <div class="max-h-[284px] overflow-y-auto">
                      <ListboxOptions>
                        <!-- Options for categories -->
                        <ListboxOption
                            v-slot="{ active, selected }"
                            v-for="(category, key) in categories"
                            :key="key"
                            :value="category"
                            as="template"
                        >
                          <li :class="[active ? '' : 'text-text-color-light-100', 'relative cursor-pointer select-none p-3 flex items-center justify-start gap-2 font-medium']">
                            <span :class="[selected ? 'rounded-full w-[24px] h-[24px] ring ring-[7px] ring-inset ring-border-color-200 bg-surface-color-100' : 'rounded-full w-[24px] h-[24px] ring ring-[1px] ring-inset ring-border-color-light-300 bg-surface-color-100']"></span>
                            <span class="block truncate font-medium">{{ category.n }}</span>
                          </li>
                        </ListboxOption>
                      </ListboxOptions>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
            <svg
                :class="{ 'lg:opacity-0': open }"
                class="w-[1px] h-[64px] max-lg:hidden"
            >
              <use xlink:href="#line" href="#line"></use>
            </svg>
          </div>
        </Listbox>
        <!-- Dropdown for Region Selection -->
        <Listbox
            v-model="selectedSubregion"
            @change="handleSubregionChange"
            :class="{'class-when-open': open}"
            v-slot="{ open }"
        >
          <div class="lg:flex w-auto relative gap-[1px]">
            <div>
              <ListboxButton
                  :class="{ '!ring-[2px] !ring-border-color-200': open }"
                  class="p-5 relative w-[320px] md:w-[512px] lg:w-[240px] xl:w-[256px] flex justify-between items-center rounded-lg text-left gap-3 ring-0 ring-border-color-dark-200 hover:bg-surface-color-300 focus:ring-[2px] focus:ring-border-color-200 bg-surface-color-100"

              >
                <span class="block truncate text-text-color-light-100 text-base font-medium">{{ selectedSubregion.n }}</span>
                <svg :class="{'rotate-180 relative': open}" class="w-4 h-4 fill-zinc-600">
                  <use xlink:href="#search-arrow" href="#search-arrow"></use>
                </svg>
              </ListboxButton>
              <transition
                  leave-active-class="transition duration-100 ease-in"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
              >
                <div v-if="open" class="z-50 absolute mt-2 w-full md:min-w-[384px] rounded-lg bg-surface-color-100 box-shadow border border-border-color-light-300 focus:outline-none sm:text-sm">
                  <div class="p-2">
                    <div class="max-h-[284px] overflow-y-auto">
                      <ListboxOptions>
                          <template
                            v-for="region in geographies"
                            :key="region.id"
                        >
                          <!-- Region labels -->
                          <li class="cursor-default select-none p-3 text-text-color-light-100 font-bold">{{ region.n }}</li>
                          <!-- Subregion options -->
                          <ListboxOption
                              v-slot="{ selected }"
                              v-for="subregion in region.sub"
                              :key="subregion.id"
                              :value="subregion"
                              :class="['relative cursor-pointer select-none p-3 flex items-center justify-start gap-2 font-medium']"
                          >
                          <span :class="[
                            selected ? 'rounded-full w-[24px] h-[24px] ring ring-[7px] ring-inset ring-border-color-200 bg-surface-color-100' : 'rounded-full w-[24px] h-[24px] ring ring-[1px] ring-inset ring-border-color-light-300 bg-surface-color-100',
                            {'text-orange-300': selectedSubregion.id === subregion.id, 'text-lime-500': selectedSubregion.id !== subregion.id}]">
                          </span>
                            {{ subregion.n }}
                          </ListboxOption>
                        </template>
                      </ListboxOptions>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
            <svg :class="{ 'opacity-0': open }"
                 class="w-[1px] h-[64px] max-lg:hidden">
              <use xlink:href="#line" href="#line"></use>
            </svg>
          </div>
        </Listbox>
        <!-- Dropdown for Area Selection with expandable subareas -->
        <Listbox
            v-model="selectedAreas"
            :multiple="true"
            @change="updateFormAreas"
            :class="{ 'class-when-open': open }"
            v-slot="{ open }"
        >
          <div class="w-[320px] md:w-[512px] lg:w-[360px] relative py-2 lg:p-0">
            <ListboxButton
                ref="areaListbox"
                :class="{ '!ring-[2px] !ring-border-color-200': open }"
                class="p-5 relative w-full flex justify-between items-center rounded-lg text-left gap-3 ring-0 ring-border-color-dark-200 hover:bg-surface-color-300  focus:ring-[2px] focus:ring-border-color-200 bg-surface-color-100"
            >
              <!-- Display selected areas or placeholder text -->
              <span class="flex justify-between items-center truncate text-text-color-light-100 text-bases font-medium leading-6">
                  <template v-if="filteredSelectedAreas.length > 0">
                     <svg class="w-6 h-6 fill-zinc-700 mr-2 hidden md:flex">
                      <use xlink:href="#pin" href="#pin"></use>
                    </svg>
                    <template v-for="(area, index) in filteredSelectedAreas">
                      <template v-if="index < 2">
                        {{ area.n }}<span v-if="index === 0 && filteredSelectedAreas.length > 1">, &nbsp; </span>
                      </template>
                    </template>
                    <template v-if="filteredSelectedAreas.length > 2">
                      +{{ filteredSelectedAreas.length - 2 }} {{ props.translations.areasPlaceholder }}
                    </template>
                  </template>
                  <template v-else>
                    <svg class="w-6 h-6 fill-zinc-700 mr-2">
                      <use xlink:href="#pin" href="#pin"></use>
                    </svg>
                    <span class="text-text-color-light-300">
                      {{ props.placeholderText }}
                    </span>
                  </template>
                </span>
            </ListboxButton>
            <transition
                leave-active-class="transition duration-100 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
              <!-- Options for selecting areas -->
              <div v-if="open" class="z-50 absolute mt-2 w-full min-w-[124px] rounded-lg bg-surface-color-100 box-shadow border border-border-color-light-300 focus:outline-none sm:text-sm">
                <div class="p-2">
                  <div class="max-h-[284px] overflow-y-auto">
                    <ListboxOptions>
                      <template v-for="area in groupedAreas" :key="area.id">
                          <!-- Expandable areas -->
                          <div class="w-full cursor-pointer p-3 text-text-color-light-100 font-medium flex items-center gap-2 hover:bg-surface-color-300 rounded-md">
                            <div class="checkbox-group">
                              <input type="checkbox" class="peer checkbox-input relative w-6 h-6 shrink-0 justify-center bg-surface-color-100 hover:border-border-color-200 appearance-none rounded border border-border-color-light-200 transition-all cursor-pointer checked:border-border-color-200 checked:bg-surface-color-700 indeterminate:border-border-color-200 indeterminate:bg-surface-color-500 indeterminate:after:content-[''] indeterminate:after:w-4 indeterminate:after:h-[3px] indeterminate:after:rounded indeterminate:after:absolute indeterminate:after:top-1/2 indeterminate:after:left-1/2 indeterminate:after:transform indeterminate:after:-translate-x-1/2 indeterminate:after:-translate-y-1/2 indeterminate:after:bg-surface-color-800 indeterminate:after:rounded-[2px]" :checked="isSelected(area)" :indeterminate.prop="isIntermediate(area)" @change.stop="toggleAreaSelection(area)" />
                              <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none" class="absolute w-6 h-6 top-0 left-0 pointer-events-none hidden peer-checked:block">
                                <path d="M15.5 4.5L7.25 12.5L3.5 8.86364" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                              </svg>
                            </div>

                            <template v-if="area.sub && area.sub.length > 0">
                              <div @click="toggleArea(area.id)" class="w-full flex items-center gap-1 leading-5	text-text-color-light-100 font-medium">
                                {{ area.n }}
                                <svg :class="{'rotate-180': expandedAreas.includes(area.id)}" class="w-4 h-4 ml-auto fill-zinc-600 transition-transform duration-200">
                                  <use xlink:href="#search-arrow" href="#search-arrow"></use>
                                </svg>
                              </div>
                            </template>
                            <template v-else>{{ area.n }}</template>
                          </div>
                          <div v-if="expandedAreas.includes(area.id)">
                            <!-- Subareas -->
                            <div
                                @click="toggleAreaSelection(subarea)"
                                v-for="subarea in area.sub"
                                :key="subarea.id"
                                class="relative bg-surface-color-200 rounded hover:bg-surface-color-300 cursor-pointer select-none p-3 flex items-center justify-start gap-2 pl-10"
                            >
                              <div
                                  class="checkbox-group items-center"
                              >
                                <input :checked="isSelected(subarea)"
                                       type="checkbox"
                                       class="peer checkbox-input relative w-6 h-6 shrink-0 justify-center bg-surface-color-100 hover:border-border-color-200 appearance-none rounded border border-border-color-light-200 transition-all cursor-pointer checked:border-border-color-200 checked:bg-surface-color-700 disabled:border-border-color-200 disabled:cursor-not-allowed pointer-events-none"
                                       @change.stop="toggleAreaSelection(subarea)"/>
                                <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none" class="absolute w-6 h-6 top-0 left-0 pointer-events-none hidden peer-checked:block">
                                  <path d="M15.5 4.5L7.25 12.5L3.5 8.86364" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                                </svg>
                                <span class="block truncate text-text-color-light-100 font-medium leading-6">{{ subarea.n }}</span>
                              </div>
                            </div>
                          </div>
                      </template>
                    </ListboxOptions>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </Listbox>
        <div class="lg:hidden w-full h-[1px] bg-surface-color-550"> </div>
        <!-- Submit button -->
        <div class="max-lg:w-full lg:ml-6 p-4 lg:p-0 -z-1">
          <div class="form-group">
            <button type="submit" class="btn btn-primary w-full lg:max-w-[480px] lg:w-auto flex btn btn-lg btn-primary rounded-full" data-gtm="search-btn">
              <svg class="w-6 h-6 fill-surface-color-100" aria-hidden="true">
                <use xlink:href="#search"></use>
              </svg>
              {{ props.translations.search2 }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script setup>
import { ref, watch, computed, onMounted, nextTick } from 'vue';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
import RadioGroupComponent from './components/RadioGroupComponent.vue';
import { buildUrl } from './utils';

// Define props for the component
const props = defineProps({
  listingTypeSale: String,
  listingTypeRental: String,
  placeholderText: String,
  submitButtonText: String,
  submitButtonLink: String,
  search2: String,
  translations: Object,
  categories: Object,
  geographies: Object,
  checkCookie: String,
  filteredAutosuggest: String,
  agentIds: String,
  initialValues: String
});

// Initial read of the JSON object
const initialValues = ref(JSON.parse(props.initialValues || '{}'));
// Check and update default values
const listingType = ref(initialValues.value?.listingType || 'sale');
const categories = ref(JSON.parse(props.categories || '{}'));
const selectedCategory = ref(categories.value[initialValues.value?.category] || categories.value['residential']);
const geographies = ref(JSON.parse(props.geographies || '[]'));

// Initialize selected subregion and selected areas
const selectedSubregion = ref(null);
const selectedAreas = ref([]);

// Function to initialize selected subregion and areas
function initializeSelections() {
  if (initialValues.value?.areaIDs && initialValues.value.areaIDs.length > 0) {
    const areaID = parseInt(initialValues.value.areaIDs[0]);
    const subregion = findSubregionById(areaID);
    selectedSubregion.value = subregion;
  } else {
    selectedSubregion.value = geographies.value[0]?.sub[0];
  }
}

function findSubregionById(id) {
  for (const region of geographies.value) {
    const subregion = region.sub.find(sub => sub.id === id);
    if (subregion) {
      return subregion;
    }
  }
  return null;
}

// Function to initialize selected areas from initial values
function initializeSelectedAreas() {
  if (initialValues.value?.autosuggestLevel4AndLevel5) {
    const areaIDs = initialValues.value.autosuggestLevel4AndLevel5;
    const selectedObjects = [];

    // Loop through each area ID and find the corresponding area object
    areaIDs.forEach(id => {
      const area = flatAreas.value.find(a => a.id.toString() === id.toString());
      if (area) {
        selectedObjects.push(area);
      }
    });

    // Set the selected areas to the found area objects
    selectedAreas.value = selectedObjects;
  }
}

initializeSelections();

// Function to get the key from a value in an object
function getKeyFromValue(value, object) {
  return Object.keys(object).find(key => object[key].n === value);
}

// Watcher to update the form when selectedCategory changes
watch(selectedCategory, (newCategory) => {
  form.value.category = newCategory.n;
});

// Watcher to update the form when selectedSubregion changes
watch(selectedSubregion, (newSubregion) => {
  if (newSubregion) {
    form.value.subregion = newSubregion.id;
    const parentRegion = geographies.value.find(region => region.sub.some(subregion => subregion.id === newSubregion.id));
    form.value.region = parentRegion ? parentRegion.id : null;
    clearSelectedAreas();
    fetchAreaData().then(() => {
      openMultiselect();
    });
  }
});

// Initialize form data
const form = ref({
  listingType: listingType.value,
  category: selectedCategory.value.n,
  region: selectedSubregion.value ? selectedSubregion.value.parentId : geographies.value[0]?.id,
  subregion: selectedSubregion.value ? selectedSubregion.value.id : geographies.value[0]?.sub[0]?.id,
});

// Initialize filtered autosuggest
const filteredAutosuggest = props.filteredAutosuggest;
const flatAreas = ref([]);
const CSRF_TOKEN = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

// Function to fetch area data from the server
async function fetchAreaData() {
  try {
    const response = await fetch(`${filteredAutosuggest}`, {
      method: "POST",
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'x-csrf-token': CSRF_TOKEN,
      },
      body: JSON.stringify({
        agentIds: props.agentIds,
        level2GeographyId: form.value.subregion,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      flatAreas.value = data.flatMap(area => [area, ...area.sub.map(sub => ({ ...sub, parentId: area.id }))]);
      groupedAreas.value = organizeAreas(flatAreas.value);
    } else {
      console.error('Failed to fetch areas');
    }
  } catch (e) {
    console.warn(e);
  }
}

// Function to clear selected areas
function clearSelectedAreas() {
  selectedAreas.value = [];
}

// Initialize grouped areas
const groupedAreas = ref([]);

// Function to organize areas into a parent-child structure
function organizeAreas(areas) {
  const areaMap = {};
  areas.forEach(area => {
    if (!area.parentId) {
      areaMap[area.id] = { ...area, sub: [] };
    } else if (areaMap[area.parentId]) {
      areaMap[area.parentId].sub.push(area);
    }
  });
  return Object.values(areaMap);
}

// Function to update the selected areas in the form
function updateFormAreas(newAreas) {
  selectedAreas.value = newAreas;
}

// Function to clear subareas when the first level is selected again
function clearSubareasOnFirstLevelSelection(newArea) {
  // Check if the new area is a first level area (no parentId)
  if (!newArea.parentId) {
    // Clear all subareas that belong to the new first level area
    selectedAreas.value = selectedAreas.value.filter(
        selected => !selected.parentId || selected.parentId !== newArea.id
    );
  }
}

// Function to toggle area selection (including subareas)
function toggleAreaSelection(area) {
  if (!Array.isArray(selectedAreas.value)) {
    selectedAreas.value = [];
  }

  function toggleAreaSelection(subarea) {
    if (isSelected(subarea)) {
      selectedAreas.value = selectedAreas.value.filter(selected => selected.id !== subarea.id);
    } else {
      selectedAreas.value.push(subarea);
    }
  }

  const isCurrentlySelected = isSelected(area);

  if (isCurrentlySelected) {
    selectedAreas.value = selectedAreas.value.filter(selected => selected.id !== area.id);
  } else {
    selectedAreas.value.push(area);
    clearSubareasOnFirstLevelSelection(area);  // Clear subareas when a first level area is selected again
  }

  if (area.sub && area.sub.length > 0) {
    area.sub.forEach(subarea => {
      if (isCurrentlySelected) {
        selectedAreas.value = selectedAreas.value.filter(selected => selected.id !== subarea.id);
      } else {
        selectedAreas.value.push(subarea);
      }
    });
  }

  updateParentSelection(area);
}

// Function to check if an area is selected
function isSelected(area) {
  return Array.isArray(selectedAreas.value) && selectedAreas.value.some(selected => selected.id === area.id);
}

// Function to check if an area is partially selected (intermediate state)
function isIntermediate(area) {
  const subareas = area.sub || [];
  const selectedSubareas = subareas.filter(subarea => isSelected(subarea));
  return selectedSubareas.length > 0 && selectedSubareas.length < subareas.length;
}

// Function to update parent area selection based on subarea selections
function updateParentSelection(area) {
  if (!area.parentId) return;

  const parentArea = groupedAreas.value.find(a => a.id === area.parentId);

  if (parentArea) {
    const allSubSelected = parentArea.sub.every(subarea => isSelected(subarea));

    if (allSubSelected) {
      if (!isSelected(parentArea)) {
        selectedAreas.value.push(parentArea);
      }
    } else {
      selectedAreas.value = selectedAreas.value.filter(selected => selected.id !== parentArea.id);
    }
  }
}

// Compute filtered selected areas (removing duplicate subareas and subs when parent is selected)
const filteredSelectedAreas = computed(() => {
  const uniqueAreas = [];
  const areaIds = new Set();

  selectedAreas.value.forEach(area => {
    // Add the area if it is not already in the set
    if (!areaIds.has(area.id)) {
      // Add the area ID to the set
      areaIds.add(area.id);

      // Check if the area has a parent
      if (area.parentId) {
        // Check if the parent is already in the set
        if (!areaIds.has(area.parentId)) {
          // Only add the subarea if the parent is not already selected
          if (!selectedAreas.value.some(selected => selected.id === area.parentId)) {
            uniqueAreas.push(area);
          }
        }
      } else {
        uniqueAreas.push(area);
      }
    }
  });

  return uniqueAreas;
});

// Function to handle subregion change and open multiselect for areas
function handleSubregionChange(newSubregion) {
  updateFormRegion(newSubregion);
  nextTick(() => {
    openMultiselect();
  });
}

const areaListbox = ref(null);
const expandedAreas = ref([]);

// Function to toggle the expansion of an area to show/hide its subareas
function toggleArea(areaId) {
  if (expandedAreas.value.includes(areaId)) {
    expandedAreas.value = expandedAreas.value.filter(id => id !== areaId);
  } else {
    expandedAreas.value.push(areaId);
  }
}

// Function to open the multiselect dropdown for areas
function openMultiselect() {
  nextTick(() => {
    if (areaListbox.value) {
      areaListbox.value.$el.dispatchEvent(new Event('click'));
    }
  });
}

// Function to handle form submission
function submitForm() {
  const baseUrl = props.submitButtonLink;
  const categoryKey = getKeyFromValue(selectedCategory.value.n, categories.value);
  const params = {
    listingType: form.value.listingType,
    listingType2: form.value.listingType,
    category: categoryKey,
    region: selectedSubregion.value.id,
    municipality: selectedAreas.value.filter(area => !area.parentId).map(area => area.id),
    neighborhood: selectedAreas.value.filter(area => area.parentId).map(area => area.id)
  };

  const url = buildUrl(baseUrl, params);
  window.open(url, '_self');
}

// Fetch area data on component mount
onMounted(() => {
  fetchAreaData().then(() => {
    // Initialize selected areas after fetching area data
    initializeSelectedAreas();
  });
});
</script>
